import React, { FormEventHandler } from 'react';
import { Button, ButtonGroup, Modal } from '@ver-uds/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { DropdownControl, Form } from '@ver-uds/uswds-react';
import LoaderIcon from 'uswds/dist/img/loader.svg';
import styled from 'styled-components';
import { useNaicsSectorsQuery, useNaicsSubSectorsQuery } from '../services';

export type LookupNaicsCodeModalProps = {
  open: boolean;
  close(): void;
  onSubmit: SubmitHandler<FormValues>;
};

type FormValues = {
  naicsSector: string;
  naicsSubSector: string;
};

// TODO: replace with `flex-align-self-stretch` className once we upgrade to USWDS 2.9.0
const BackgroundBar = styled.span`
  align-self: stretch;
`;

const LookupNaicsCodeModal = ({ open, close, onSubmit }: LookupNaicsCodeModalProps): JSX.Element => {
  const id = 'lookup-naics-code';
  const formId = `${id}-form`;

  const { isLoading: naicsSectorsIsLoading, data: naicsSectorsData } = useNaicsSectorsQuery();

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { register, handleSubmit, watch, errors } = useForm<FormValues>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      naicsSector: undefined,
      naicsSubSector: undefined,
    },
  });

  const { naicsSector, naicsSubSector } = watch();

  const {
    isIdle: naicsSubSectorsIsIdle,
    isLoading: naicsSubSectorsIsLoading,
    data: naicsSubSectorsData,
  } = useNaicsSubSectorsQuery(naicsSector);

  const onSubmitForm: FormEventHandler = (event) => {
    event.stopPropagation();

    return handleSubmit(async (data) => {
      await onSubmit(data);
      close();
    })(event);
  };

  return (
    <Modal id={id} header="Look Up NAICS Code" open={open} close={close} fixedToTop>
      <Modal.Content>
        <Form className="maxw-none" onSubmit={onSubmitForm} id={formId} noValidate>
          {naicsSectorsIsLoading ? (
            <>
              Loading… <img src={LoaderIcon} alt="" className="height-2" />
            </>
          ) : (
            <>
              <DropdownControl
                id={`${id}--naics-sector-id`}
                name="naicsSector"
                className="grid-col-12 tablet:grid-col-10 desktop:grid-col-9"
                label="Sector"
                labelHint={
                  <span className="usa-sr-only">A sub-sector field will be displayed after selecting a sector.</span>
                }
                required
                ref={register({
                  required: {
                    value: true,
                    message: 'A selection is required.',
                  },
                })}
                errorId={errors.naicsSector && 'naics-sector-error'}
                errorMessage={errors.naicsSector?.message}
              >
                <option value="">- Select -</option>
                {naicsSectorsData
                  ?.sort(({ displayName: a }, { displayName: b }) => a.localeCompare(b))
                  .map(({ code, displayName }) => (
                    <option key={code} value={code}>
                      {displayName} ({code})
                    </option>
                  ))}
              </DropdownControl>

              <div hidden={naicsSubSectorsIsIdle || naicsSubSectorsIsLoading}>
                <DropdownControl
                  id={`${id}--naics-subsector-id`}
                  name="naicsSubSector"
                  className="grid-col-12 tablet:grid-col-10 desktop:grid-col-9"
                  label="Sub-Sector"
                  required
                  ref={register({
                    required: {
                      value: true,
                      message: 'A selection is required.',
                    },
                  })}
                  errorId={errors.naicsSubSector && 'naics-sector-error'}
                  errorMessage={errors.naicsSubSector?.message}
                >
                  <option value="">- Select -</option>
                  {naicsSubSectorsData
                    ?.sort(({ displayName: a }, { displayName: b }) => a.localeCompare(b))
                    .map(({ code, displayName }) => (
                      <option key={code} value={code}>
                        {displayName} ({code})
                      </option>
                    ))}
                </DropdownControl>
              </div>
            </>
          )}
        </Form>
      </Modal.Content>
      <Modal.Footer>
        <div className="display-none tablet:display-flex padding-y-05 flex-align-center">
          <span>NAICS Code</span> <BackgroundBar className="width-1px margin-x-105 bg-base-lighter" />{' '}
          <span data-testid={`${id}--subsector-or-sector`} className="font-alt-lg text-bold">
            {naicsSubSector || naicsSector || '---'}
          </span>
        </div>
        <ButtonGroup>
          <Button type="button" variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" form={formId} onClick={onSubmitForm}>
            Done
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

// istanbul ignore if: Not used for test environment; Modal.setAppElement should be called in the tests directly.
if (process.env.NODE_ENV !== 'test') {
  Modal.setAppElement('#root');
}

export default LookupNaicsCodeModal;
